import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LangToggle } from "./LangToggle";
import { NAV } from "../../data/content";
import { useLang } from "../../i18n/LangContext";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled || open ? "border-b border-[#E7E2D7] bg-[#FBF9F5]/85 backdrop-blur-md" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="header-logo-link" aria-label="Lunghe Frequenze APS - Home">
          <Logo />
        </Link>
        <div className="hidden items-center gap-9 md:flex">
        <nav className="flex items-center gap-9">
          {NAV.slice(1).map((to) => (
            <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} className="group relative py-2 text-sm text-stone-700 transition-colors hover:text-[#1C1917]">
              {({ isActive }) => (
                <>
                  {t.nav[to]}
                  <span className={`absolute -bottom-0.5 left-0 h-px bg-[#C85A32] transition-[width] duration-500 ${isActive ? "w-full" : "w-0 group-hover:w-full"}`} />
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <LangToggle />
        </div>
        <div className="flex items-center gap-3 md:hidden">
        <LangToggle id="mobile" />
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D6CEBF]"
          aria-label={t.menu}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden md:hidden"
          >
            <div className="flex flex-col px-5 pb-8 pt-2">
              {NAV.map((to, i) => (
                <motion.div key={to} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i + 0.1 }}>
                  <NavLink to={to} end data-testid={`mobile-nav-${to.slice(1) || "home"}`} className={({ isActive }) => `block border-b border-[#E7E2D7] py-4 font-serif text-3xl ${isActive ? "text-[#C85A32]" : "text-[#1C1917]"}`}>
                    {t.nav[to]}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
